import { useState, useCallback, useRef, useEffect } from "react";
import { T, Badge, Card, Btn, KV, DataTable, StatCard, fmtCcy, fmtDate, fmtDateTime, font, mono, ST, Inp, Sel } from '../theme.js';
import { SARStatus } from '../engines/compliance-engine.js';
import { AlertStatus } from '../engines/monitoring-engine.js';
import { GenerateView, DQReportView, HistoryView, StatusCodesView, DepositorLinksView } from './SCVModule.jsx';
import { api } from '../api-client.js';


const COMPLIANCE_NAV = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'sars', label: 'SAR Management' },
  { id: 'dsar', label: 'DSAR' },
  { id: 'reports', label: 'Reports' },
  { id: 'scv', label: 'SCV' },
];

const SCV_SUB_NAV = [
  { id: 'scv-generate', label: 'Generate' },
  { id: 'scv-dq', label: 'DQ Report' },
  { id: 'scv-history', label: 'History' },
  { id: 'scv-codes', label: 'Status Codes' },
  { id: 'scv-links', label: 'Depositor Links' },
];

const sarStatusColor = s => ({ DRAFT: 'gray', UNDER_REVIEW: 'amber', SUBMITTED: 'blue', ACKNOWLEDGED: 'green' }[s] || 'gray');

export default function ComplianceModule({ compliance, kyc, monitoring, refresh: parentRefresh }) {
  const [view, setView] = useState('dashboard');
  const [selectedSAR, setSelectedSAR] = useState(null);
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState(null);
  const toastRef = useRef(null);

  const refresh = useCallback(() => {
    setTick(t => t + 1);
    if (parentRefresh) parentRefresh();
  }, [parentRefresh]);

  const showToast = useCallback((msg, type = 'success') => {
    if (toastRef.current) clearTimeout(toastRef.current);
    setToast({ msg, type });
    toastRef.current = setTimeout(() => setToast(null), 3500);
  }, []);
  const showError = useCallback(msg => showToast(msg, 'error'), [showToast]);

  const [dqGenId, setDqGenId] = useState(null);

  const openSAR = (sarId) => { setSelectedSAR(sarId); setView('detail'); };
  const backToList = () => { setSelectedSAR(null); setView('sars'); };
  const openDqReport = useCallback((genId) => { setDqGenId(genId); setView('scv-dq'); }, []);

  const sarCounts = compliance.getComplianceDashboard();

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 1000, padding: '10px 16px',
          background: toast.type === 'error' ? T.redBg : T.greenBg,
          border: `1px solid ${toast.type === 'error' ? T.red : T.green}40`,
          borderRadius: 6, fontSize: 12, maxWidth: 420, fontFamily: font }}>
          <span style={{ color: toast.type === 'error' ? T.red : T.green, marginRight: 8 }}>{toast.type === 'error' ? '✗' : '✓'}</span>{toast.msg}
        </div>
      )}

      {/* Primary tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: view.startsWith('scv') ? 0 : 20, borderBottom: `1px solid ${T.border}`, overflowX: 'auto' }}>
        {COMPLIANCE_NAV.map(n => {
          const isActive = view === n.id || (view === 'detail' && n.id === 'sars') || (view.startsWith('scv') && n.id === 'scv');
          return (
          <button key={n.id} onClick={() => { setView(n.id === 'scv' ? 'scv-generate' : n.id); setSelectedSAR(null); }} style={{
            padding: '8px 14px', fontSize: 12, fontWeight: isActive ? 600 : 400,
            color: isActive ? T.blue : T.textSecondary,
            background: 'transparent', border: 'none',
            borderBottom: isActive ? `2px solid ${T.blue}` : '2px solid transparent',
            cursor: 'pointer', fontFamily: font, marginBottom: -1, whiteSpace: 'nowrap',
          }}>
            {n.label}
            {n.id === 'sars' && sarCounts.draft + sarCounts.underReview > 0 && (
              <span style={{ marginLeft: 6, background: T.amber, color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: 9, fontWeight: 700, lineHeight: '16px', display: 'inline-block' }}>
                {sarCounts.draft + sarCounts.underReview}
              </span>
            )}
          </button>
        );})}
      </div>

      {/* SCV sub-tabs (only visible when an SCV view is active) */}
      {view.startsWith('scv') && (
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, overflowX: 'auto', paddingLeft: 8 }}>
          {SCV_SUB_NAV.map(n => (
            <button key={n.id} onClick={() => setView(n.id)} style={{
              padding: '6px 12px', fontSize: 11, fontWeight: view === n.id ? 600 : 400,
              color: view === n.id ? T.blue : T.textSecondary,
              background: 'transparent', border: 'none',
              borderBottom: view === n.id ? `2px solid ${T.blue}` : '2px solid transparent',
              cursor: 'pointer', fontFamily: font, marginBottom: -1, whiteSpace: 'nowrap',
            }}>
              {n.label}
            </button>
          ))}
        </div>
      )}

      {view === 'dashboard' && <ComplianceDashboard compliance={compliance} kyc={kyc} monitoring={monitoring} openSAR={openSAR} key={tick} />}
      {view === 'sars' && <SARListView compliance={compliance} kyc={kyc} openSAR={openSAR} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'detail' && selectedSAR && <SARDetailView compliance={compliance} kyc={kyc} monitoring={monitoring} sarId={selectedSAR} back={backToList} refresh={refresh} showToast={showToast} showError={showError} key={tick} />}
      {view === 'dsar' && <DSARView key={tick} showToast={showToast} showError={showError} refresh={refresh} />}
      {view === 'reports' && <ReportsView compliance={compliance} kyc={kyc} monitoring={monitoring} key={tick} />}
      {view === 'scv-generate' && <GenerateView key={tick} showToast={showToast} refresh={refresh} openDqReport={openDqReport} />}
      {view === 'scv-dq' && <DQReportView key={`dq-${dqGenId}-${tick}`} generationId={dqGenId} showToast={showToast} />}
      {view === 'scv-history' && <HistoryView key={tick} openDqReport={openDqReport} />}
      {view === 'scv-codes' && <StatusCodesView key={tick} showToast={showToast} refresh={refresh} />}
      {view === 'scv-links' && <DepositorLinksView key={tick} showToast={showToast} refresh={refresh} />}
    </div>
  );
}


// ============================================================
// DASHBOARD
// ============================================================

function ComplianceDashboard({ compliance, kyc, monitoring, openSAR }) {
  const dash = compliance.getComplianceDashboard();
  const openAlerts = monitoring?.getAllOpenAlerts?.() || [];
  const escalated = (monitoring?.alerts || []).filter(a => a.status === AlertStatus.ESCALATED);

  return (
    <div>
      <ST>Compliance Overview</ST>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Draft SARs" value={dash.draft} color={T.textMuted} />
        <StatCard label="Under Review" value={dash.underReview} color={T.amber} />
        <StatCard label="Submitted to NCA" value={dash.submitted} color={T.blue} />
        <StatCard label="Acknowledged" value={dash.acknowledged} color={T.green} />
        <StatCard label="Overdue" value={dash.overdue} sub=">14 days open" color={T.red} />
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        <Card style={{ flex: 1 }}>
          <ST>SARs by Customer Risk</ST>
          <div style={{ display: 'flex', gap: 16, padding: '8px 0' }}>
            {['HIGH', 'MEDIUM', 'LOW'].map(r => (
              <div key={r} style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: r === 'HIGH' ? T.red : r === 'MEDIUM' ? T.amber : T.green }}>{dash.byRisk[r] || 0}</div>
                <div style={{ fontSize: 11, color: T.textMuted }}>{r}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card style={{ flex: 1 }}>
          <ST>Monitoring Pipeline</ST>
          <div style={{ display: 'flex', gap: 16, padding: '8px 0' }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.red }}>{openAlerts.length}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Open Alerts</div>
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.purple }}>{escalated.length}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Escalated</div>
            </div>
          </div>
        </Card>
      </div>

      {dash.recentSARs.length > 0 && (
        <Card>
          <ST>Recent SARs</ST>
          <DataTable
            headers={['SAR ID', 'Customer', 'Status', 'Reason', 'Created']}
            rows={dash.recentSARs.map(s => {
              const cust = kyc?.getCustomer(s.customerId);
              return [
                <span style={{ cursor: 'pointer', color: T.blue, fontSize: 11, fontFamily: mono }} onClick={() => openSAR(s.id)}>{s.id.slice(0, 16)}...</span>,
                cust?.legalName || s.customerId,
                <Badge color={sarStatusColor(s.status)}>{s.status}</Badge>,
                <span style={{ fontSize: 11 }}>{s.reason.slice(0, 40)}{s.reason.length > 40 ? '...' : ''}</span>,
                fmtDate(s.createdAt),
              ];
            })}
            compact
          />
        </Card>
      )}
    </div>
  );
}


// ============================================================
// SAR LIST
// ============================================================

function SARListView({ compliance, kyc, openSAR, refresh, showToast, showError }) {
  const [filter, setFilter] = useState('ALL');
  const allSARs = compliance.getAllSARs();
  const filtered = filter === 'ALL' ? allSARs : allSARs.filter(s => s.status === filter);

  const [showCreate, setShowCreate] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState('');
  const [newReason, setNewReason] = useState('');

  const customers = kyc?.getAllCustomers() || [];

  const handleCreate = async () => {
    try {
      const sar = await compliance.createSAR({ customerId: newCustomerId, alertIds: [], reason: newReason, createdBy: 'compliance_user' });
      showToast(`SAR ${sar.id.slice(0, 12)} created`);
      setShowCreate(false);
      setNewCustomerId('');
      setNewReason('');
      await refresh();
    } catch (e) { showError(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ST>Suspicious Activity Reports</ST>
        <Btn onClick={() => setShowCreate(!showCreate)} small>New SAR</Btn>
      </div>

      {showCreate && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Sel label="Customer" value={newCustomerId} onChange={e => setNewCustomerId(e.target.value)}
              options={[{ value: '', label: 'Select customer...' }, ...customers.map(c => ({ value: c.id, label: c.legalName }))]} />
            <Inp label="Reason" value={newReason} onChange={e => setNewReason(e.target.value)} placeholder="Reason for SAR..." />
            <Btn onClick={handleCreate} small>Create</Btn>
          </div>
        </Card>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {['ALL', SARStatus.DRAFT, SARStatus.UNDER_REVIEW, SARStatus.SUBMITTED, SARStatus.ACKNOWLEDGED].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '4px 10px', fontSize: 11, border: `1px solid ${filter === f ? T.blue : T.border}`,
            background: filter === f ? T.blueBg : 'transparent', color: filter === f ? T.blue : T.textSecondary,
            borderRadius: 4, cursor: 'pointer', fontFamily: font,
          }}>{f === 'ALL' ? `All (${allSARs.length})` : `${f} (${allSARs.filter(s => s.status === f).length})`}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>No SARs found</div>
      ) : (
        <DataTable
          headers={['SAR ID', 'Customer', 'Status', 'Reason', 'Amount', 'Created', 'Submitted']}
          rows={filtered.map(s => {
            const cust = kyc?.getCustomer(s.customerId);
            return [
              <span style={{ cursor: 'pointer', color: T.blue, fontSize: 11, fontFamily: mono }} onClick={() => openSAR(s.id)}>{s.id.slice(0, 16)}...</span>,
              cust?.legalName || s.customerId,
              <Badge color={sarStatusColor(s.status)}>{s.status}</Badge>,
              <span style={{ fontSize: 11 }}>{s.reason.slice(0, 30)}</span>,
              s.amountInvolved ? fmtCcy(s.amountInvolved) : '—',
              fmtDate(s.createdAt),
              s.submittedAt ? fmtDate(s.submittedAt) : '—',
            ];
          })}
          compact
        />
      )}
    </div>
  );
}


// ============================================================
// SAR DETAIL
// ============================================================

function SARDetailView({ compliance, kyc, monitoring, sarId, back, refresh, showToast, showError }) {
  const sar = compliance.getSAR(sarId);

  const [narrative, setNarrative] = useState(sar?.narrative || '');
  const [activity, setActivity] = useState(sar?.suspiciousActivity || '');
  const [amount, setAmount] = useState(sar?.amountInvolved || '');
  const [periodFrom, setPeriodFrom] = useState(sar?.reportingPeriod?.from || '');
  const [periodTo, setPeriodTo] = useState(sar?.reportingPeriod?.to || '');

  if (!sar) return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>SAR not found</div>;

  const customer = kyc?.getCustomer(sar.customerId);
  const kycCase = kyc?.getKYCCase(sar.customerId);
  const linkedAlerts = (monitoring?.alerts || []).filter(a => sar.alertIds.includes(a.id));
  const editable = sar.status === SARStatus.DRAFT || sar.status === SARStatus.UNDER_REVIEW;

  const save = async () => {
    try {
      await compliance.updateSAR(sar.id, {
        narrative,
        suspiciousActivity: activity,
        amountInvolved: amount ? parseFloat(amount) : null,
        reportingPeriod: periodFrom && periodTo ? { from: periodFrom, to: periodTo } : null,
      });
      showToast('SAR updated');
      await refresh();
    } catch (e) { showError(e.message); }
  };

  const handleReview = async () => {
    try { await compliance.reviewSAR(sar.id, 'compliance_user'); showToast('Moved to Under Review'); await refresh(); }
    catch (e) { showError(e.message); }
  };

  const handleSubmit = async () => {
    try {
      await save();
      await compliance.submitSAR(sar.id, 'compliance_user');
      showToast('SAR submitted to NCA');
      await refresh();
    } catch (e) { showError(e.message); }
  };

  const handleAcknowledge = async () => {
    const ref = prompt('Enter NCA reference:');
    if (!ref) return;
    try { await compliance.acknowledgeSAR(sar.id, ref); showToast('SAR acknowledged'); await refresh(); }
    catch (e) { showError(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Btn onClick={back} variant="ghost" small>&larr; Back</Btn>
        <ST>SAR Detail</ST>
        <Badge color={sarStatusColor(sar.status)}>{sar.status}</Badge>
        {sar.ncaReference && <span style={{ fontSize: 11, color: T.green, fontFamily: mono }}>NCA: {sar.ncaReference}</span>}
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <Card style={{ flex: 1 }}>
          <ST>Subject</ST>
          <KV label="Customer" value={customer?.legalName || sar.customerId} />
          <KV label="Type" value={customer?.type || '—'} />
          <KV label="Risk Rating" value={kycCase?.riskRating || '—'} />
          <KV label="KYC Status" value={kycCase?.status || '—'} />
          <KV label="Registration" value={customer?.registrationNumber || '—'} />
        </Card>
        <Card style={{ flex: 1 }}>
          <ST>SAR Metadata</ST>
          <KV label="SAR ID" value={<span style={{ fontFamily: mono, fontSize: 11 }}>{sar.id}</span>} />
          <KV label="Created" value={fmtDateTime(sar.createdAt)} />
          <KV label="Created By" value={sar.createdBy} />
          <KV label="Reason" value={sar.reason} />
          {sar.submittedAt && <KV label="Submitted" value={fmtDateTime(sar.submittedAt)} />}
          {sar.submittedBy && <KV label="Submitted By" value={sar.submittedBy} />}
        </Card>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <ST>Suspicious Activity</ST>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Activity Type</label>
          <input value={activity} onChange={e => setActivity(e.target.value)} disabled={!editable}
            placeholder="e.g. Layering, structuring, unusual velocity..."
            style={{ width: '100%', padding: '6px 8px', fontSize: 12, background: editable ? T.bg : T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font, boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Narrative</label>
          <textarea value={narrative} onChange={e => setNarrative(e.target.value)} disabled={!editable}
            placeholder="Full narrative describing the suspicious activity, patterns observed, and why this warrants reporting..."
            rows={6}
            style={{ width: '100%', padding: '6px 8px', fontSize: 12, background: editable ? T.bg : T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Amount Involved</label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} disabled={!editable}
              placeholder="0.00"
              style={{ padding: '6px 8px', fontSize: 12, background: editable ? T.bg : T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font, width: 140 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Period From</label>
            <input type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)} disabled={!editable}
              style={{ padding: '6px 8px', fontSize: 12, background: editable ? T.bg : T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Period To</label>
            <input type="date" value={periodTo} onChange={e => setPeriodTo(e.target.value)} disabled={!editable}
              style={{ padding: '6px 8px', fontSize: 12, background: editable ? T.bg : T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font }} />
          </div>
        </div>
      </Card>

      {linkedAlerts.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <ST>Linked Alerts ({linkedAlerts.length})</ST>
          <DataTable
            headers={['Alert ID', 'Type', 'Severity', 'Status', 'Created']}
            rows={linkedAlerts.map(a => [
              <span style={{ fontFamily: mono, fontSize: 11 }}>{a.id.slice(0, 14)}</span>,
              a.type,
              <Badge color={a.severity === 'CRITICAL' || a.severity === 'HIGH' ? 'red' : a.severity === 'MEDIUM' ? 'amber' : 'gray'}>{a.severity}</Badge>,
              <Badge color={a.status === 'OPEN' ? 'red' : a.status === 'ESCALATED' ? 'purple' : 'green'}>{a.status}</Badge>,
              fmtDate(a.createdAt),
            ])}
            compact
          />
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <ST>Status History</ST>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sar.statusHistory.map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12 }}>
              <Badge color={sarStatusColor(h.status)}>{h.status}</Badge>
              <span style={{ color: T.textSecondary }}>{fmtDateTime(h.at)}</span>
              <span style={{ color: T.textMuted }}>by {h.by}</span>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 8 }}>
        {editable && <Btn onClick={save}>Save</Btn>}
        {sar.status === SARStatus.DRAFT && <Btn onClick={handleReview} variant="secondary">Move to Review</Btn>}
        {(sar.status === SARStatus.DRAFT || sar.status === SARStatus.UNDER_REVIEW) && <Btn onClick={handleSubmit} variant="primary">Submit to NCA</Btn>}
        {sar.status === SARStatus.SUBMITTED && <Btn onClick={handleAcknowledge} variant="secondary">Record NCA Acknowledgement</Btn>}
      </div>
    </div>
  );
}


// ============================================================
// REPORTS
// ============================================================

function ReportsView({ compliance, kyc, monitoring }) {
  const [reportType, setReportType] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [ctrCustomer, setCtrCustomer] = useState('');
  const [periodFrom, setPeriodFrom] = useState('2025-06-01');
  const [periodTo, setPeriodTo] = useState('2025-06-30');

  const customers = kyc?.getAllCustomers() || [];

  const generateCTR = async () => {
    if (!ctrCustomer) return;
    try {
      const data = await compliance.generateCTR(ctrCustomer, new Date(periodFrom), new Date(periodTo));
      setReportData(data);
      setReportType('CTR');
    } catch (e) { setReportData({ error: e.message }); setReportType('CTR'); }
  };

  const generateHighRisk = async () => {
    const data = await compliance.getHighRiskCustomerReport();
    setReportData(data);
    setReportType('HIGH_RISK');
  };

  const generateAlertResolution = async () => {
    const data = await compliance.getAlertResolutionReport(new Date(periodFrom), new Date(periodTo));
    setReportData(data);
    setReportType('ALERT_RESOLUTION');
  };

  return (
    <div>
      <ST>Regulatory Reports</ST>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Card style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Currency Transaction Report</div>
          <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 12 }}>Transaction summary for a customer over a period.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <Sel value={ctrCustomer} onChange={e => setCtrCustomer(e.target.value)}
              options={[{ value: '', label: 'Customer...' }, ...customers.map(c => ({ value: c.id, label: c.legalName }))]} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)}
              style={{ padding: '4px 6px', fontSize: 11, background: T.bg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font }} />
            <input type="date" value={periodTo} onChange={e => setPeriodTo(e.target.value)}
              style={{ padding: '4px 6px', fontSize: 11, background: T.bg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font }} />
          </div>
          <Btn onClick={generateCTR} small>Generate CTR</Btn>
        </Card>

        <Card style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>High-Risk Customer Report</div>
          <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 12 }}>All HIGH risk customers with alert and SAR counts.</div>
          <Btn onClick={generateHighRisk} small>Generate</Btn>
        </Card>

        <Card style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Alert Resolution Report</div>
          <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 12 }}>Alert closure rates and resolution times.</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)}
              style={{ padding: '4px 6px', fontSize: 11, background: T.bg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font }} />
            <input type="date" value={periodTo} onChange={e => setPeriodTo(e.target.value)}
              style={{ padding: '4px 6px', fontSize: 11, background: T.bg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 4, fontFamily: font }} />
          </div>
          <Btn onClick={generateAlertResolution} small>Generate</Btn>
        </Card>
      </div>

      {reportData && reportType === 'CTR' && !reportData.error && (
        <Card>
          <ST>CTR — {reportData.customerName}</ST>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>{reportData.periodStart} to {reportData.periodEnd}</div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <StatCard label="Transactions" value={reportData.summary.totalTransactions} />
            <StatCard label="Inbound" value={fmtCcy(reportData.summary.totalInbound)} sub={`${reportData.summary.inboundCount} payments`} color={T.green} />
            <StatCard label="Outbound" value={fmtCcy(reportData.summary.totalOutbound)} sub={`${reportData.summary.outboundCount} payments`} color={T.red} />
          </div>
          {reportData.transactions.length > 0 && (
            <DataTable
              headers={['Amount', 'Direction', 'Scheme', 'Counterparty', 'Timestamp']}
              rows={reportData.transactions.map(t => [
                fmtCcy(t.amount),
                <Badge color={t.direction === 'INBOUND' ? 'green' : 'red'}>{t.direction}</Badge>,
                t.scheme,
                t.counterparty || '—',
                fmtDateTime(t.timestamp),
              ])}
              compact
            />
          )}
        </Card>
      )}

      {reportData && reportType === 'CTR' && reportData.error && (
        <Card><div style={{ color: T.red, fontSize: 12 }}>{reportData.error}</div></Card>
      )}

      {reportData && reportType === 'HIGH_RISK' && (
        <Card>
          <ST>High-Risk Customer Report ({reportData.length} customers)</ST>
          {reportData.length === 0 ? (
            <div style={{ color: T.textMuted, fontSize: 12, padding: 12 }}>No high-risk customers found.</div>
          ) : (
            <DataTable
              headers={['Customer', 'KYC Status', 'Open Alerts', 'Total Alerts', 'Active SARs', 'Next Review']}
              rows={reportData.map(r => [
                r.legalName,
                <Badge color={r.kycStatus === 'APPROVED' ? 'green' : 'amber'}>{r.kycStatus}</Badge>,
                r.openAlerts > 0 ? <span style={{ color: T.red, fontWeight: 600 }}>{r.openAlerts}</span> : '0',
                r.totalAlerts,
                r.activeSARs > 0 ? <span style={{ color: T.amber, fontWeight: 600 }}>{r.activeSARs}</span> : '0',
                r.nextReviewDate ? fmtDate(r.nextReviewDate) : '—',
              ])}
              compact
            />
          )}
        </Card>
      )}

      {reportData && reportType === 'ALERT_RESOLUTION' && (
        <Card>
          <ST>Alert Resolution Report</ST>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>{reportData.periodStart} to {reportData.periodEnd}</div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <StatCard label="Total Alerts" value={reportData.totalAlerts} />
            <StatCard label="Closed" value={reportData.closedCount} color={T.green} />
            <StatCard label="Open" value={reportData.openCount} color={T.red} />
            <StatCard label="Escalated" value={reportData.escalatedCount} color={T.purple} />
            <StatCard label="Closure Rate" value={`${reportData.closureRate}%`} color={T.blue} />
            <StatCard label="FP Rate" value={`${reportData.falsePositiveRate}%`} sub="of closed" color={T.textMuted} />
            <StatCard label="Avg Resolution" value={`${reportData.avgResolutionHours}h`} color={T.amber} />
          </div>
          <ST>By Severity</ST>
          <div style={{ display: 'flex', gap: 12 }}>
            {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(s => (
              <div key={s} style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: s === 'CRITICAL' ? T.red : s === 'HIGH' ? T.amber : s === 'MEDIUM' ? T.blue : T.textMuted }}>{reportData.bySeverity[s]}</div>
                <div style={{ fontSize: 10, color: T.textMuted }}>{s}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}


// ============================================================
// DSAR VIEW
// ============================================================

const dsarStatusColor = s => ({ RECEIVED: 'gray', IDENTITY_VERIFIED: 'blue', IN_PROGRESS: 'amber', COMPLETED: 'green', REFUSED: 'red', EXTENDED: 'amber' }[s] || 'gray');

function DSARView({ showToast, showError, refresh }) {
  const [dsars, setDsars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [fulfilling, setFulfilling] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setDsars(await api.get('/gdpr/dsar')); }
    catch (e) { showError('Failed to load DSARs'); }
    finally { setLoading(false); }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const selectDsar = useCallback(async (id) => {
    setSelected(id);
    setSummary(null);
    try {
      const d = await api.get(`/gdpr/dsar/${id}`);
      setDetail(d);
      // Try to load summary if exists
      try {
        const s = await api.get(`/gdpr/dsar/${id}/export/summary`);
        setSummary(s);
      } catch { /* no export yet */ }
    } catch (e) { showError('Failed to load DSAR'); }
  }, [showError]);

  const fulfilDsar = useCallback(async () => {
    if (!selected) return;
    setFulfilling(true);
    try {
      const result = await api.post(`/gdpr/dsar/${selected}/fulfil`);
      setSummary(result.summary);
      showToast('DSAR fulfilled successfully');
      await selectDsar(selected);
      await load();
    } catch (e) { showError(e.message || 'Fulfilment failed'); }
    finally { setFulfilling(false); }
  }, [selected, showToast, showError, selectDsar, load]);

  const daysRemaining = (dueDate) => {
    if (!dueDate) return null;
    return Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  if (selected && detail) {
    const days = daysRemaining(detail.dueDate);
    const isOverdue = days !== null && days < 0;
    const isUrgent = days !== null && days >= 0 && days < 5;

    return (
      <div>
        <Btn onClick={() => { setSelected(null); setDetail(null); setSummary(null); }} variant="secondary" small>Back to List</Btn>
        <div style={{ marginTop: 16 }}>
          <ST>DSAR Detail</ST>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <KV label="ID" value={detail.id} />
                <KV label="Type" value={detail.requestType} />
                <KV label="Status" value={<Badge color={dsarStatusColor(detail.status)}>{detail.status}</Badge>} />
                <KV label="Received" value={fmtDate(detail.receivedDate)} />
                <KV label="Due Date" value={fmtDate(detail.dueDate)} />
                {detail.responseDate && <KV label="Response Date" value={fmtDate(detail.responseDate)} />}
                {detail.handledBy && <KV label="Handled By" value={detail.handledBy} />}
              </div>
              <div style={{ textAlign: 'right' }}>
                {days !== null && (
                  <div style={{
                    fontSize: 24, fontWeight: 700, fontFamily: mono,
                    color: isOverdue ? T.red : isUrgent ? T.amber : T.green,
                    marginBottom: 4,
                  }}>
                    {isOverdue ? `${Math.abs(days)}d OVERDUE` : `${days}d remaining`}
                  </div>
                )}
                <div style={{ fontSize: 11, color: T.textMuted }}>30-day statutory deadline</div>
              </div>
            </div>

            {detail.responseSummary && (
              <div style={{ padding: '8px 12px', background: T.greenBg, border: `1px solid ${T.green}30`, borderRadius: 6, fontSize: 12, marginBottom: 12 }}>
                {detail.responseSummary}
              </div>
            )}

            {detail.status !== 'COMPLETED' && detail.status !== 'REFUSED' && detail.requestType === 'ACCESS' && (
              <Btn onClick={fulfilDsar} disabled={fulfilling} small>
                {fulfilling ? 'Processing...' : 'Start Fulfilment'}
              </Btn>
            )}

            {detail.status === 'COMPLETED' && (
              <div style={{ marginTop: 16 }}>
                <ST>Export</ST>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Btn onClick={() => window.open(`/api/gdpr/dsar/${detail.id}/export/json`, '_blank')} variant="secondary" small>Download JSON</Btn>
                  <Btn onClick={() => window.open(`/api/gdpr/dsar/${detail.id}/export/pdf`, '_blank')} variant="secondary" small>Download PDF</Btn>
                </div>
              </div>
            )}

            {summary && (
              <div style={{ marginTop: 16 }}>
                <ST>Fulfilment Summary</ST>
                <Card>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                    {[
                      ['Accounts', summary.accountCount],
                      ['Transactions', summary.transactionCount],
                      ['Payments', summary.paymentCount],
                      ['Facilities', summary.facilityCount],
                      ['Beneficiaries', summary.beneficiaryCount],
                      ['Standing Orders', summary.standingOrderCount],
                      ['Complaints', summary.complaintCount],
                      ['Alerts', summary.alertCount],
                      ['Contacts', summary.contactCount],
                      ['Statements', summary.statementCount],
                      ['Audit Entries', summary.auditEntryCount],
                    ].map(([label, count]) => (
                      <div key={label} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, fontFamily: mono, color: T.text }}>{count}</div>
                        <div style={{ fontSize: 10, color: T.textMuted }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ST>Data Subject Access Requests</ST>
      {loading && <div style={{ fontSize: 12, color: T.textMuted }}>Loading...</div>}
      {!loading && dsars.length === 0 && <div style={{ fontSize: 12, color: T.textMuted }}>No DSAR requests.</div>}
      {!loading && dsars.length > 0 && (
        <DataTable
          columns={[
            { key: 'id', label: 'ID', render: v => <span style={{ fontFamily: mono, fontSize: 10 }}>{v.substring(0, 8)}...</span> },
            { key: 'requestType', label: 'Type' },
            { key: 'status', label: 'Status', render: (v, r) => <Badge color={dsarStatusColor(v)}>{v}</Badge> },
            { key: 'receivedDate', label: 'Received', render: v => fmtDate(v) },
            { key: 'dueDate', label: 'Due', render: v => {
              const days = daysRemaining(v);
              const color = days !== null && days < 0 ? T.red : days !== null && days < 5 ? T.amber : T.text;
              return <span style={{ color, fontWeight: days !== null && days < 5 ? 600 : 400 }}>{fmtDate(v)}</span>;
            }},
            { key: 'handledBy', label: 'Handler' },
            { key: '_action', label: '', render: (_, r) => <Btn onClick={() => selectDsar(r.id)} variant="secondary" small>View</Btn> },
          ]}
          data={dsars}
        />
      )}
    </div>
  );
}
